// M5.5 / M5.7 follow-up: per-ingestor transitions page, scoped
// to a single Jira ingestor instance. Reachable from the
// "Transitions" button on the IngestorDetailPage (Jira ingestors
// only — status transitions are a Jira-specific concept).
//
// Why scoped to the ingestor instead of the team: status
// transitions are a Jira-specific feature (the matrix is keyed
// on Jira status names like "In Review" or "Done"), so showing
// the matrix in the admin top-level nav was confusing — admins
// without a Jira ingestor had no use for the page, and admins
// WITH one had to figure out which team to pick. Putting the
// matrix under the Jira ingestor makes the connection obvious:
// "this team has a Jira ingestor; here are the transitions we
// score on its events."
//
// The team_id is implicit (the ingestor already knows which team
// it belongs to). The Jira instance id is passed straight to the
// matrix so the from/to pickers can offer the project's actual
// status names as a <datalist>.

import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { TransitionsMatrix } from './TransitionsMatrix';
import { ingestorsApi } from './ingestors/api';
import type { IngestorInstance } from './ingestors/types';

export function IngestorTransitionsPage() {
  const { id = '' } = useParams();

  const inst = useQuery<{ instance: IngestorInstance }>({
    queryKey: ['ingestor-instance', id],
    queryFn: () => ingestorsApi.getInstance(id),
    enabled: !!id,
  });

  // Team name for the breadcrumb. The matrix uses team_id, not
  // name, but the header reads better with the human label.
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: Array<{ id: string; name: string }> }>('/api/teams'),
  });
  const team = (teams.data?.teams ?? []).find((t) => t.id === inst.data?.instance?.team_id);

  if (inst.isLoading) return <p className="text-ink-muted">Loading…</p>;
  if (inst.isError || !inst.data) return <p className="text-accent-danger">Failed to load ingestor.</p>;
  const i = inst.data.instance;
  // Defensive: the IngestorDetailPage only shows the "Transitions"
  // button for type=jira, but the route exists and the api would
  // happily render the matrix for a github ingestor too. If the
  // type isn't jira, redirect to the detail page with a clear
  // message (status transitions are Jira-specific).
  if (i.type !== 'jira') {
    return (
      <div className="card space-y-2">
        <h1 className="text-lg font-semibold">Status transitions are Jira-only</h1>
        <p className="text-sm text-ink-muted">
          The <code>{i.type}</code> ingestor does not emit status change events. Status transition
          scoring only applies to Jira ingestors.
        </p>
        <Link className="btn-primary inline-block" to={`/admin/ingestors/${i.id}`}>
          Back to ingestor
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <Link to={`/admin/ingestors/${i.id}`} className="text-sm text-ink-muted hover:text-ink">
          ← {i.name}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold">
          Status transitions <span className="text-ink-muted text-base font-normal">— {team?.name ?? 'team'}</span>
        </h1>
        <p className="mt-1 text-sm text-ink-muted">
          Award XP when a teammate moves a Jira issue from one status to another. The from/to
          pickers suggest the project's actual status names; leave <em>From</em> empty to match
          any source status.
        </p>
      </header>

      <TransitionsMatrix
        teamId={i.team_id}
        jiraInstanceId={i.id}
        showJiraIngestorHint={false}
      />
    </div>
  );
}
